/**
 * Per-node automatic retry pipeline.
 *
 * Pass 1 has already run (full-canvas html2canvas with svg-word for text).
 * This orchestrator measures per-text diff, and for text elements whose
 * diffRatio exceeds threshold:
 *
 *   Attempt 2 → re-rasterize JUST that element with foreignObject strategy,
 *              composite into pass-1 at the element's bbox.
 *   Attempt 3 → re-rasterize JUST that element with foreignObject inside
 *              an extra clipping wrapper (WebKit overflow guard).
 *
 * Never re-runs html2canvas over the full canvas. Never rasterizes the
 * full canvas as a fallback. Each retry is per-node, capped by a retry
 * budget + time budget, with explicit canvas disposal.
 *
 * Output:
 *   {
 *     canvas: HTMLCanvasElement,   // pass-1 canvas, mutated in place
 *     attempts: [{ id, attempt, strategy, diff, accepted, reason? }, …],
 *   }
 */

import html2canvas from 'html2canvas';
import { serializeTextAsForeignObject } from './exportTextForeignObject';
import { setCachedStrategy, computeTextSignature } from './exportBrowserEngine';

const DEFAULTS = {
  textDiffRatio:      0.02,   // per-text mismatch ratio that triggers retry
  maxRetriesPerNode:  2,      // attempt 2 + attempt 3 max
  totalTimeBudgetMs:  15000,  // hard cap on the retry phase
  perNodeTimeBudgetMs: 3000,  // single-node ceiling
};

// Build an off-screen container holding a clone of the live element with
// canvas-positioning stripped (so the element lays out at 0,0 inside the
// container). Returns the container, with the clone as its only child.
function buildIsolatedContainer(liveEl) {
  const w = liveEl.offsetWidth;
  const h = liveEl.offsetHeight;
  if (w <= 0 || h <= 0) return null;

  const container = document.createElement('div');
  container.style.cssText = [
    'position: fixed',
    'left: -99999px',
    'top: -99999px',
    `width: ${w}px`,
    `height: ${h}px`,
    'overflow: hidden',
    'background: transparent',
    'pointer-events: none',
    'contain: layout style',
  ].join(';');
  document.body.appendChild(container);

  const clone = liveEl.cloneNode(true);
  // Strip canvas-positioning so the clone lays out at (0,0).
  clone.style.position = 'static';
  clone.style.left = '0';
  clone.style.top  = '0';
  // Keep the element's own rotation only if the bbox math accounts for
  // it — for now strip rotation so the rasterized output is axis-aligned
  // and composites cleanly. Rotated elements are deferred (logged below).
  clone.style.transform = 'none';

  container.appendChild(clone);
  return { container, clone, w, h };
}

async function rasterizeWithStrategy(liveEl, strategy, exportScale) {
  const built = buildIsolatedContainer(liveEl);
  if (!built) return null;
  const { container, clone, w, h } = built;

  try {
    if (strategy === 'foreignObject') {
      // Run the foreignObject serializer ON THE CLONE; that's the same
      // function the main pipeline would use, so behaviour matches.
      serializeTextAsForeignObject(clone, liveEl, []);
    } else if (strategy === 'foreignObject-clipped') {
      // Stricter variant: wrap in explicit clip-path inset(0) so WebKit
      // doesn't let foreignObject content escape its bounds.
      serializeTextAsForeignObject(clone, liveEl, []);
      const svgChild = clone.querySelector('svg');
      if (svgChild) {
        svgChild.style.clipPath = 'inset(0)';
        svgChild.style.webkitClipPath = 'inset(0)';
      }
      clone.style.overflow = 'hidden';
      clone.style.clipPath = 'inset(0)';
      clone.style.webkitClipPath = 'inset(0)';
    } else if (strategy === 'svg-word' || strategy === 'native-html') {
      // Last-resort: leave the clone as raw HTML and let html2canvas's
      // native text path render it. This is what svg-word was emulating
      // anyway; if both foreignObject variants have failed for a node,
      // the native path is our remaining option per the brief's
      // "fallback isolation" rule (this node only — never the canvas).
      // No serialization applied; clone retains its inner HTML subtree.
    }

    return await html2canvas(container, {
      width: w,
      height: h,
      scale: exportScale,
      backgroundColor: null,
      useCORS: true,
      logging: false,
    });
  } finally {
    if (container.parentNode) container.parentNode.removeChild(container);
  }
}

// Per-node diff against the live canvas reference. Returns ratio in [0,1].
function computeNodeDiff(refData, candData, w, h, bbox, tolerance = 12) {
  const x0 = Math.max(0, bbox.x);
  const y0 = Math.max(0, bbox.y);
  const x1 = Math.min(w, bbox.x + bbox.w);
  const y1 = Math.min(h, bbox.y + bbox.h);
  if (x1 <= x0 || y1 <= y0) return { ratio: 0, diff: 0, total: 0 };
  let diff = 0, total = 0;
  for (let y = y0; y < y1; y += 2) {
    for (let x = x0; x < x1; x += 2) {
      const i = (y * w + x) * 4;
      const d = Math.abs(refData[i] - candData[i])
              + Math.abs(refData[i + 1] - candData[i + 1])
              + Math.abs(refData[i + 2] - candData[i + 2]);
      total++;
      if (d > tolerance * 3) diff++;
    }
  }
  return { ratio: total ? diff / total : 0, diff, total };
}

export async function autoRetryFailingTextElements({
  pass1Canvas,
  perElementDiff,
  perTextResults,
  liveCanvas,
  exportScale = 2,
  liveReferenceCanvas,   // canvas of the live editor at same scale
  thresholds = {},
}) {
  const T = { ...DEFAULTS, ...thresholds };
  const attempts = [];

  if (!pass1Canvas || !Array.isArray(perElementDiff) || !liveCanvas) {
    return { canvas: pass1Canvas, attempts, skipped: 'missing-inputs' };
  }

  const failing = perElementDiff.filter(
    (e) => e.type === 'text' && e.diffRatio > T.textDiffRatio,
  );
  if (failing.length === 0) {
    return { canvas: pass1Canvas, attempts };
  }

  // Pre-read both pixel buffers ONCE — every per-node diff measure reuses them.
  const w = pass1Canvas.width, h = pass1Canvas.height;
  const refCtx  = liveReferenceCanvas?.getContext?.('2d');
  const passCtx = pass1Canvas.getContext('2d');
  const refData = refCtx ? refCtx.getImageData(0, 0, w, h).data : null;

  // Index per-text strategy info so cache-population knows what strategy
  // a successful render used.
  const perTextById = new Map();
  (perTextResults || []).forEach((r) => { if (r?.id) perTextById.set(r.id, r); });

  const phaseStart = Date.now();
  // Pass-1 default is now foreignObject. Escalation paths for failing
  // nodes: try the stricter clipped variant (Safari overflow guard); if
  // still failing, fall back to the legacy svg-word reconstructor as a
  // last resort. svg-word is per-node only — never replaces the whole
  // canvas's text rendering.
  const ESCALATION = ['foreignObject-clipped', 'svg-word'];

  for (const fail of failing) {
    if (Date.now() - phaseStart > T.totalTimeBudgetMs) {
      attempts.push({ id: fail.id, skipped: 'total-time-budget' });
      break;
    }
    const liveEl = liveCanvas.querySelector(
      `[data-export-id="${CSS.escape(fail.id)}"]`,
    );
    if (!liveEl) {
      attempts.push({ id: fail.id, skipped: 'no-live-element' });
      continue;
    }
    // Skip rotated elements for now — bbox-based composite won't account
    // for rotation; logged so it's visible in diagnostics.
    const xform = (liveEl.style.transform || '').trim();
    if (xform && xform !== 'none' && /rotate|matrix|skew/i.test(xform)) {
      attempts.push({ id: fail.id, skipped: 'rotated-deferred', transform: xform });
      continue;
    }

    // Pass-1 baseline diagnostic for this element.
    attempts.push({
      id: fail.id, attempt: 1,
      strategy: perTextById.get(fail.id)?.strategy || 'svg-word',
      diff: +fail.diffRatio.toFixed(4),
      accepted: false,
    });

    let accepted = false;
    let lastDiff = fail.diffRatio;
    const nodeStart = Date.now();

    for (let i = 0; i < Math.min(T.maxRetriesPerNode, ESCALATION.length); i++) {
      if (Date.now() - nodeStart > T.perNodeTimeBudgetMs) {
        attempts.push({ id: fail.id, attempt: 2 + i, skipped: 'per-node-time-budget' });
        break;
      }
      const strategy = ESCALATION[i];
      let retryCanvas = null;
      try {
        retryCanvas = await rasterizeWithStrategy(liveEl, strategy, exportScale);
      } catch (e) {
        attempts.push({ id: fail.id, attempt: 2 + i, strategy, error: String(e?.message || e) });
        continue;
      }
      if (!retryCanvas) {
        attempts.push({ id: fail.id, attempt: 2 + i, strategy, skipped: 'rasterize-null' });
        continue;
      }

      // Composite: clear region, draw retry canvas.
      passCtx.save();
      passCtx.clearRect(fail.bbox.x, fail.bbox.y, fail.bbox.w, fail.bbox.h);
      passCtx.drawImage(retryCanvas, fail.bbox.x, fail.bbox.y);
      passCtx.restore();

      // Measure post-retry diff against the live reference.
      let postDiff = null;
      if (refData) {
        const candData = passCtx.getImageData(0, 0, w, h).data;
        postDiff = computeNodeDiff(refData, candData, w, h, fail.bbox).ratio;
      }
      lastDiff = postDiff;

      const isAccepted = postDiff === null || postDiff <= T.textDiffRatio;
      attempts.push({
        id: fail.id, attempt: 2 + i, strategy,
        diff: postDiff !== null ? +postDiff.toFixed(4) : null,
        accepted: isAccepted,
      });

      // Memory: dispose retry canvas immediately.
      retryCanvas.width = 0;
      retryCanvas.height = 0;

      if (isAccepted) {
        // Cache the winning strategy for this element signature so future
        // exports skip the escalation entirely.
        try {
          const cs = window.getComputedStyle(liveEl);
          const sig = computeTextSignature(liveEl, cs);
          if (sig) setCachedStrategy(sig, strategy);
        } catch { /* cache write is best-effort */ }
        accepted = true;
        break;
      }
    }

    if (!accepted) {
      attempts.push({ id: fail.id, finalDiff: lastDiff !== null ? +lastDiff.toFixed(4) : null, accepted: false, exhausted: true });
    }
  }

  return { canvas: pass1Canvas, attempts };
}
